/**
 * Client-side preflight for story cover uploads.
 * Validates before upload; always sends the user's original file as the archival master.
 * Optional createPreviewUrl for instant UI feedback without altering the upload payload.
 */

import {
  CUSTOMER_IMAGE_FORMATS_LINE,
  CUSTOMER_IMAGE_MIME_TYPES,
  CUSTOMER_IMAGE_UNSUPPORTED_MESSAGE,
  IMAGE_PREVIEW_UNSUPPORTED_HINT,
  isCustomerImageFile,
} from './imageUploadValidation';

export const STORY_COVER_ALLOWED_TYPES = CUSTOMER_IMAGE_MIME_TYPES;
export const STORY_COVER_MAX_BYTES = 30 * 1024 * 1024; // keep in sync with backend
export const STORY_COVER_MAX_DIMENSION = 12000;
export const STORY_COVER_MAX_PIXELS = 50_000_000;

export function isStoryCoverMimeAllowed(file) {
  return isCustomerImageFile(file);
}

/**
 * Read image dimensions without uploading. Uses object URL — does not modify the file.
 */
export function readImageDimensions(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(IMAGE_PREVIEW_UNSUPPORTED_HINT));
    };
    img.src = url;
  });
}

/**
 * Validate file for story cover upload. Returns { ok, error?, dimensions?, previewUnsupported? }.
 */
export async function preflightStoryCoverFile(file) {
  if (!file) {
    return { ok: false, error: 'No file selected' };
  }
  if (!isStoryCoverMimeAllowed(file)) {
    return { ok: false, error: CUSTOMER_IMAGE_UNSUPPORTED_MESSAGE };
  }
  if (file.size > STORY_COVER_MAX_BYTES) {
    const mb = Math.round(STORY_COVER_MAX_BYTES / (1024 * 1024));
    return { ok: false, error: `Photo must be ${mb} MB or smaller` };
  }
  let dimensions;
  try {
    dimensions = await readImageDimensions(file);
  } catch (err) {
    if (isStoryCoverMimeAllowed(file)) {
      return { ok: true, dimensions: null, previewUnsupported: true, previewHint: err.message };
    }
    return { ok: false, error: 'Invalid or unsupported image file' };
  }
  const { width, height } = dimensions;
  if (Math.max(width, height) > STORY_COVER_MAX_DIMENSION) {
    return {
      ok: false,
      error: `Photo is too large (${width}×${height}). Longest edge must be ${STORY_COVER_MAX_DIMENSION.toLocaleString()} px or less`,
    };
  }
  if (width * height > STORY_COVER_MAX_PIXELS) {
    return { ok: false, error: 'Photo has too many pixels. Try a smaller image.' };
  }
  return { ok: true, dimensions };
}

/** Instant local preview URL — caller should revoke when done. */
export function createStoryCoverPreviewUrl(file) {
  return URL.createObjectURL(file);
}

/**
 * Format byte size for upload progress hints.
 */
export function formatUploadSize(bytes) {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export { CUSTOMER_IMAGE_FORMATS_LINE };
